// Content creation — video and photo for a client's social, on retainer.
//
// Second pass. The first ran eleven sections and framed the offer as social
// media management, which is not the business: HDDN LAB makes the content,
// and posting it is an add-on. This page says that plainly and does it in six
// sections: the offer, the proof, what you get and what it costs, one-off
// work, what a month looks like, and how to start.
//
// One number, not a tier ladder. Nobody else in this category publishes a
// figure at all, and a single honest floor keeps that advantage without
// pricing scopes that genuinely vary.

import React from 'react';
import { Bay, Btn, Card, Eyebrow, Figures, Head, OtherDoor, Ticks } from '../primitives.jsx';
import { useSiteData } from '../data.js';
import { go } from '../routes.js';
import { CONTACT_EMAIL } from '../../contact';

function StartingPrice({ retainer }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
      <span className="hddn-label" style={{ color: 'var(--accent-ink)' }}>Starting at</span>
      <span style={{
        fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 'var(--step-4)',
        fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.04em', lineHeight: 1,
      }}>
        ${Number(retainer.startingPrice).toLocaleString()}
      </span>
      <span className="hddn-muted" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--step-1)' }}>
        {retainer.cadence || '/mo'}
      </span>
    </div>
  );
}

export default function Content() {
  const d = useSiteData();
  const { caseStudy, copy, retainer, alaCarte, reporting } = d;

  return (
    <>
      <Bay band="ground">
        <Eyebrow>Content creation</Eyebrow>
        <h1 className="hddn-h1" style={{ marginTop: 18, maxWidth: '16ch' }}>
          We make the content. You post it.
        </h1>
        <p className="hddn-lede" style={{ marginTop: 22, maxWidth: '54ch' }}>
          Video and photo for your social, made on a monthly retainer by a team that lives here.
          One shoot day a month, at your place or in our studio, delivered ready to post. If you
          would rather we posted it too, that is an add-on.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 30 }}>
          <Btn href="#pricing" variant="fill">See what you get and what it costs</Btn>
          <Btn href="#proof" variant="line">See the numbers first</Btn>
        </div>
      </Bay>

      <Bay band="raised" id="proof">
        <Head
          eyebrow={`${caseStudy.months} months · ${caseStudy.client}`}
          title="Content that moved real numbers."
          lede={`${caseStudy.window}. We made and ran the content for a whole downtown. Most agency sites claim quality; these are the absolute figures.`}
        />
        <Figures items={caseStudy.headline} cols={4} />
        <div className="hddn-grid hddn-grid--sidebar" style={{ marginTop: 'var(--gap)' }}>
          <p className="hddn-lede" style={{ color: 'var(--ink)', maxWidth: '58ch' }}>
            Earned reach — 904,563 of that total — is the half you cannot buy. It happened because
            55 local businesses tagged the feed in 178 of their own posts, which only occurs when the
            people making the content actually know the block. {copy.localBody}
          </p>
          <Card>
            <p className="hddn-label">Why reels lead</p>
            <p className="hddn-h4" style={{ marginTop: 12 }}>
              Reels averaged 2,494 reach against 858 for a static post. Nearly three times.
            </p>
            <p className="hddn-note" style={{ marginTop: 12 }}>
              From our own thirteen months of reporting, not an industry deck. It is why every
              month is cut reels-first.
            </p>
          </Card>
        </div>
      </Bay>

      <Bay id="pricing">
        <Head
          eyebrow="What you get, and the number"
          title="Here is the number."
          lede="Every agency in this category makes you book a demo to find one out. We would rather you knew right now whether we are in your range."
        />
        <div className="hddn-grid hddn-grid--sidebar">
          <Card style={{ borderColor: 'var(--accent)', borderWidth: 2 }}>
            <StartingPrice retainer={retainer} />
            <hr className="hddn-rule" style={{ margin: '24px 0' }} />
            <p className="hddn-label">In every retainer</p>
            <div style={{ marginTop: 12 }}><Ticks items={retainer.included} /></div>
            {retainer.note ? <p className="hddn-note" style={{ marginTop: 18 }}>{retainer.note}</p> : null}
            <div style={{ marginTop: 24 }}>
              <Btn href="/contact?path=content" onClick={go('/contact?path=content')} variant="fill" wide>
                Get your number
              </Btn>
            </div>
          </Card>

          <div style={{ display: 'grid', gap: 'var(--gap)', alignContent: 'start' }}>
            <div>
              <p className="hddn-label">What moves it</p>
              <p className="hddn-muted" style={{ marginTop: 10, maxWidth: '46ch' }}>
                Above the floor it depends on scope, so we scope it with you rather than publish a
                tier you do not fit. What we ask on the first call:
              </p>
              <div style={{ marginTop: 16 }}><Ticks items={retainer.shapes} /></div>
            </div>
            <div>
              <p className="hddn-label">What we report each month</p>
              <p className="hddn-muted" style={{ marginTop: 10, maxWidth: '46ch' }}>
                Agreed before the first shoot, measured from month one. Whether we post or you do.
              </p>
              <div style={{ marginTop: 16 }}><Ticks items={reporting} /></div>
            </div>
            <Card>
              <p className="hddn-label">{copy.buildings.eyebrow}</p>
              <p className="hddn-h4" style={{ marginTop: 10 }}>{copy.buildings.title}</p>
              <p className="hddn-note" style={{ marginTop: 10 }}>{copy.buildings.lede}</p>
            </Card>
          </div>
        </div>
      </Bay>

      {/* One-off work. Named so people know to ask; unpriced because the
          internal rates are line items billed alongside a talent cost, and the
          number next to a service is not what a client actually pays. */}
      <Bay band="sunk" id="one-off">
        <div className="hddn-grid hddn-grid--sidebar">
          <div>
            <Head eyebrow="Not a retainer" title="Or just the one thing." />
            <p className="hddn-lede" style={{ color: 'var(--ink)', maxWidth: '52ch' }}>{alaCarte.intro}</p>
            <p className="hddn-note" style={{ marginTop: 16, maxWidth: '52ch' }}>{alaCarte.foot}</p>
            <div style={{ marginTop: 24 }}>
              <Btn href="/contact?path=content" onClick={go('/contact?path=content')} variant="line">
                Tell us what you need
              </Btn>
            </div>
          </div>
          <div className="hddn-defs">
            {alaCarte.services.map((sv) => (
              <div className="hddn-def" key={sv.name}>
                <dt style={{ fontFamily: 'var(--sans)', fontSize: 'var(--step-0)', fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: 'var(--ink)' }}>
                  {sv.name}
                </dt>
                <dd className="hddn-muted">{sv.d}</dd>
              </div>
            ))}
          </div>
        </div>
      </Bay>

      <Bay>
        <Head eyebrow="What a month looks like" title="One shoot day. Four weeks of content." />
        <div className="hddn-grid hddn-grid--2">
          {copy.month.map((m) => (
            <Card key={m.w}>
              <p className="hddn-label">{m.w}</p>
              <p className="hddn-h4" style={{ marginTop: 10 }}>{m.t}</p>
              <p className="hddn-muted" style={{ marginTop: 10 }}>{m.d}</p>
            </Card>
          ))}
        </div>
      </Bay>

      <Bay band="raised">
        <div className="hddn-grid hddn-grid--sidebar">
          <Head
            eyebrow="Next step"
            title="Tell us what you're working on."
            lede="A business, a brand, a building. Where it is, what you need, when you need it by. We will come back with what we would actually make and what it costs."
          />
          <div style={{ display: 'grid', gap: 12, alignContent: 'end' }}>
            <Btn href="/contact?path=content" onClick={go('/contact?path=content')} variant="fill" wide>Start a conversation</Btn>
            <Btn href={`mailto:${CONTACT_EMAIL}`} variant="line" wide>Email {CONTACT_EMAIL}</Btn>
          </div>
        </div>
      </Bay>

      <OtherDoor
        k="The other doors"
        title="Just need photos taken, or the room?"
        body="Our photographers shoot portraits, headshots and product, in the studio or on location. And the studio itself rents by the hour and the day."
        href="/photographer"
        cta="Book a photographer"
      />
    </>
  );
}
